import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Tooltip, Autocomplete, TextField } from "@mui/material";
import { Rocket, Loader2, Clock, Info } from "lucide-react";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import AdsAPIs from "../../apiRoutes/Ads";
import { GET_AD_LAUNCHES_QUERY_KEY } from "../../utils/constants/queryKeys";
import CustomModal from "../common/CustomModal";
import ActionConfirmation from "../common/ActionConfirmation";
import RequestedByFilter from "../common/RequestedByFilter";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DatePicker } from "../ui/date-picker";
import { Switch } from "../ui/switch";

// Mimics the shadcn SelectTrigger look for inline MUI Autocomplete usage.
const shadcnAutocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    padding: "0 8px !important",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    borderRadius: "6px",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    color: "#111827",
  },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d1d5db", borderWidth: "1px",
  },
  "& .MuiAutocomplete-input": { padding: "0 4px !important", fontSize: "14px" },
  "& .MuiAutocomplete-input::placeholder": { color: "#6b7280", opacity: 1 },
};

// "HH:MM" representation of a Date in local time.
const toTimeValue = (date) => {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Combine a calendar Date and an "HH:MM" string into a single Date. Returns null
// if either piece is missing or invalid.
const combineDateTime = (date, time) => {
  if (!date || !time) return null;
  const [h, m] = time.split(":").map((s) => parseInt(s, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
};

/**
 * Launch wizard: pick ready ads (track/category/app/requested-by filters), a Meta
 * account + campaign, adset settings and optional scheduling, then bulk-launch.
 * Owns all wizard state, queries and mutations. Keep it mounted and drive it via
 * `open` — selections (account, campaign, filters) persist across close/reopen.
 */
export default function LaunchWizardModal({ open, onClose }) {
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  const [modalRequestedByFilter, setModalRequestedByFilter] = useState("");
  const { globalApp } = useGlobalAppAndLanguage();
  const [trackFilter, setTrackFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedMetaAccountId, setSelectedMetaAccountId] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [modalSelectedIds, setModalSelectedIds] = useState(() => new Set());
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("");
  // Campaign edit panel
  const [campaignEdits, setCampaignEdits] = useState({}); // { daily_budget_rupees, bid_strategy }
  const [pendingStatus, setPendingStatus] = useState(null); // "ACTIVE" | "PAUSED" while confirming
  const [resetOffAdsetLimits, setResetOffAdsetLimits] = useState(true);
  // Adset settings
  const [adsetDailyBudgetRupees, setAdsetDailyBudgetRupees] = useState("");
  const [dailyMinSpendTargetRupees, setDailyMinSpendTargetRupees] = useState("2000");
  const [dailySpendCapRupees, setDailySpendCapRupees] = useState("6500");
  const [adsetStartDate, setAdsetStartDate] = useState(null);
  const [adsetStartTime, setAdsetStartTime] = useState("");
  const [adsetEndDate, setAdsetEndDate] = useState(null);
  const [adsetEndTime, setAdsetEndTime] = useState("");

  // Modal-only query: pulls ready ads filtered server-side by track/category.
  const { data: modalResult } = useQuery({
    queryKey: ["adLaunchModal", trackFilter, categoryFilter, globalApp, modalRequestedByFilter],
    queryFn: () => AdsAPIs.listLaunches({
      axiosPrivate,
      status: "ready",
      track: trackFilter || undefined,
      category: categoryFilter || undefined,
      requested_by_id: modalRequestedByFilter || undefined,
      limit: 200,
      offset: 0,
    }),
    enabled: open,
  });
  const modalRows = useMemo(() => modalResult?.data ?? [], [modalResult]);

  const { data: facets } = useQuery({
    queryKey: ["adLaunchFacets"],
    queryFn: () => AdsAPIs.listLaunchFacets({ axiosPrivate }),
    enabled: open,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GET_AD_LAUNCHES_QUERY_KEY });

  const bulkLaunchMutation = useMutation({
    mutationFn: ({ ad_moderate_ids, scheduled_at, adset_daily_budget, daily_min_spend_target, daily_spend_cap, start_time, end_time }) => AdsAPIs.launchAdsBulk({
      axiosPrivate,
      ad_moderate_ids,
      meta_ad_account_id: normalizedMetaAccountId,
      meta_campaign_id: selectedCampaignId,
      for_app_name: globalApp || undefined,
      track: trackFilter || undefined,
      category: categoryFilter || undefined,
      scheduled_at: scheduled_at || undefined,
      adset_daily_budget: adset_daily_budget ?? undefined,
      daily_min_spend_target: daily_min_spend_target ?? undefined,
      daily_spend_cap: daily_spend_cap ?? undefined,
      start_time: start_time || undefined,
      end_time: end_time || undefined,
    }),
    onSuccess: (res) => {
      invalidate();
      const count = res?.count ?? 0;
      const skipped = res?.skipped ?? [];
      const wasScheduled = scheduleEnabled && !!combineDateTime(scheduledDate, scheduledTime);
      toast.success(`${count} ad(s) ${wasScheduled ? "scheduled" : "queued"} for launch.`);
      if (skipped.length > 0) {
        toast(`${skipped.length} skipped: ${skipped.map((s) => `#${s.ad_moderate_id} (${s.reason})`).join(", ")}`, { icon: "⚠️" });
      }
      onClose();
      setScheduleEnabled(false);
      setScheduledDate(null);
      setScheduledTime("");
      setAdsetDailyBudgetRupees("");
      setDailyMinSpendTargetRupees("2000");
      setDailySpendCapRupees("6500");
      setAdsetStartDate(null);
      setAdsetStartTime("");
      setAdsetEndDate(null);
      setAdsetEndTime("");
    },
  });

  // Live campaign details for the selected campaign.
  const { data: campaignDetail, isFetching: campaignLoading } = useQuery({
    queryKey: ["meta_campaign", selectedCampaignId],
    queryFn: () => AdsAPIs.getMetaCampaign({ axiosPrivate, campaign_id: selectedCampaignId }),
    enabled: !!selectedCampaignId,
  });

  const updateCampaignMutation = useMutation({
    mutationFn: (payload) => AdsAPIs.updateMetaCampaign({
      axiosPrivate, campaign_id: selectedCampaignId, payload,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta_campaign", selectedCampaignId] });
      queryClient.invalidateQueries({ queryKey: ["meta_campaigns", accountIdForCampaigns] });
      setCampaignEdits({});
      setPendingStatus(null);
      toast.success("Campaign updated.");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Failed to update campaign.");
      setPendingStatus(null);
    },
  });

  const clearOffLimitsMutation = useMutation({
    mutationFn: () => AdsAPIs.clearOffAdsetLimits({
      axiosPrivate, campaign_id: selectedCampaignId,
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["meta_campaign", selectedCampaignId] });
      const cleared = res?.cleared ?? 0;
      const skipped = res?.skipped ?? 0;
      toast.success(
        `Cleared limits on ${cleared} paused adset(s)${skipped ? ` · ${skipped} skipped` : ""}.`,
      );
    },
  });

  const launchEligibleRows = modalRows;

  const modalSelectedRows = launchEligibleRows.filter((r) => modalSelectedIds.has(r.ad_moderate_id));
  const modalLaunchCount = modalSelectedRows.length;
  const modalParentAdIds = modalSelectedRows.map((r) => r.ad_id).filter((v) => v != null);
  const modalModerationIds = modalSelectedRows.map((r) => r.ad_moderate_id).filter((v) => v != null);
  const modalAdsetCount = useMemo(() => {
    const groups = new Set();
    modalSelectedRows.forEach((r) => {
      groups.add(r.ad_id != null ? `ad:${r.ad_id}` : `mod:${r.ad_moderate_id}`);
    });
    return groups.size;
  }, [modalSelectedRows]);

  const { data: metaAccounts = [] } = useQuery({
    queryKey: ["meta_ad_accounts"],
    queryFn: () => AdsAPIs.listMetaAdAccounts({ axiosPrivate }),
  });
  const normalizedMetaAccountId = selectedMetaAccountId.trim();
  const accountIdForCampaigns = normalizedMetaAccountId
    ? (normalizedMetaAccountId.startsWith("act_") ? normalizedMetaAccountId : `act_${normalizedMetaAccountId}`)
    : "";
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ["meta_campaigns", accountIdForCampaigns],
    queryFn: () => AdsAPIs.listMetaCampaigns({ axiosPrivate, ad_account_id: accountIdForCampaigns }),
    enabled: !!accountIdForCampaigns,
  });

  // Seed the selection ONCE per modal-open cycle, as soon as data arrives.
  // Without the ref guard the effect would either fire before modalRows loaded
  // (leaving nothing pre-selected) or re-fire on every filter change (wiping
  // out the user's manual unchecks).
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      didSeedRef.current = false;
      return;
    }
    if (didSeedRef.current) return;
    if (!modalResult?.data) return;
    setModalSelectedIds(new Set(modalResult.data.map((r) => r.ad_moderate_id)));
    didSeedRef.current = true;
  }, [open, modalResult]);

  // When the operator picks a track, auto-select all ads in that track.
  // This is the headline workflow: "I want to launch everything in track X."
  useEffect(() => {
    if (!trackFilter) return;
    if (!open) return;
    if (!modalResult?.data) return;
    setModalSelectedIds((prev) => {
      const next = new Set(prev);
      modalResult.data.forEach((r) => next.add(r.ad_moderate_id));
      return next;
    });
  }, [trackFilter, modalResult, open]);

  useEffect(() => {
    setSelectedCampaignId("");
  }, [selectedMetaAccountId]);

  useEffect(() => {
    setCampaignEdits({});
    setPendingStatus(null);
    setResetOffAdsetLimits(true);
  }, [selectedCampaignId]);

  // Smart default placeholder for per-adset daily budget when campaign is CBO.
  const smartAdsetBudgetPlaceholder = useMemo(() => {
    const N = modalSelectedRows.length;
    if (!campaignDetail?.has_cbo || N === 0) return "";
    const dailyPaise = campaignDetail?.daily_budget || 0;
    return String(Math.floor((dailyPaise / 100) / N));
  }, [campaignDetail, modalSelectedRows.length]);

  // Validation for start/end times.
  const adsetStartIso = useMemo(() => {
    const d = combineDateTime(adsetStartDate, adsetStartTime);
    return d ? d.toISOString() : null;
  }, [adsetStartDate, adsetStartTime]);
  const adsetEndIso = useMemo(() => {
    const d = combineDateTime(adsetEndDate, adsetEndTime);
    return d ? d.toISOString() : null;
  }, [adsetEndDate, adsetEndTime]);

  const scheduleConflictError = useMemo(() => {
    const start = combineDateTime(adsetStartDate, adsetStartTime);
    const sched = scheduleEnabled ? combineDateTime(scheduledDate, scheduledTime) : null;
    if (start && sched && start.getTime() < sched.getTime()) {
      return "Start must be at/after the scheduled dispatch time.";
    }
    return null;
  }, [adsetStartDate, adsetStartTime, scheduleEnabled, scheduledDate, scheduledTime]);

  const adsetTimeError = useMemo(() => {
    const start = combineDateTime(adsetStartDate, adsetStartTime);
    const end = combineDateTime(adsetEndDate, adsetEndTime);
    if (start && start.getTime() < Date.now()) return "Start must be in the future.";
    if (start && end && end.getTime() <= start.getTime()) return "End must be after start.";
    if (scheduleConflictError) return scheduleConflictError;
    return null;
  }, [adsetStartDate, adsetStartTime, adsetEndDate, adsetEndTime, scheduleConflictError]);

  // Min/cap validation — both optional, but if both supplied, min must be <= cap.
  const spendLimitError = useMemo(() => {
    // Min/cap fields are only active (and submitted) for CBO campaigns.
    if (!campaignDetail?.has_cbo) return null;
    const min = parseFloat(dailyMinSpendTargetRupees);
    const cap = parseFloat(dailySpendCapRupees);
    if (Number.isFinite(min) && min < 0) return "Daily min spend must be ≥ 0.";
    if (Number.isFinite(cap) && cap < 0) return "Daily spend cap must be ≥ 0.";
    if (Number.isFinite(min) && Number.isFinite(cap) && min > cap) {
      return "Daily min spend must be ≤ daily spend cap.";
    }
    return null;
  }, [dailyMinSpendTargetRupees, dailySpendCapRupees, campaignDetail]);

  // Campaign panel helpers
  const campaignDailyBudgetRupees = useMemo(() => {
    if (campaignEdits.daily_budget_rupees != null) return campaignEdits.daily_budget_rupees;
    return campaignDetail?.daily_budget ? String(Math.floor(campaignDetail.daily_budget / 100)) : "";
  }, [campaignDetail, campaignEdits]);
  // Bid strategy is locked to Lowest Cost; a fix is pending whenever the live campaign differs.
  const bidStrategyNeedsFix = !!campaignDetail?.bid_strategy && campaignDetail.bid_strategy !== "LOWEST_COST_WITHOUT_CAP";
  const campaignDirty = (
    (campaignEdits.daily_budget_rupees != null
      && Math.round(parseFloat(campaignEdits.daily_budget_rupees || "0") * 100) !== (campaignDetail?.daily_budget || 0))
    || bidStrategyNeedsFix
    || resetOffAdsetLimits
  );

  const hasCampaignEditPayload = (
    (campaignEdits.daily_budget_rupees != null
      && Math.round(parseFloat(campaignEdits.daily_budget_rupees || "0") * 100) !== (campaignDetail?.daily_budget || 0))
    || bidStrategyNeedsFix
  );

  const saveCampaign = async () => {
    const payload = {};
    if (
      campaignEdits.daily_budget_rupees != null
      && Math.round(parseFloat(campaignEdits.daily_budget_rupees || "0") * 100) !== (campaignDetail?.daily_budget || 0)
    ) {
      payload.daily_budget = Math.round(parseFloat(campaignEdits.daily_budget_rupees || "0") * 100);
    }
    // Bid strategy is always forced to Lowest Cost.
    if (campaignDetail?.bid_strategy !== "LOWEST_COST_WITHOUT_CAP") {
      payload.bid_strategy = "LOWEST_COST_WITHOUT_CAP";
    }
    if (Object.keys(payload).length === 0 && !resetOffAdsetLimits) return;

    // Clear paused adset limits FIRST so Meta won't reject the budget PATCH.
    if (resetOffAdsetLimits) {
      try {
        await clearOffLimitsMutation.mutateAsync();
      } catch {
        return; // toast already shown; don't proceed to budget update
      }
      setResetOffAdsetLimits(false);
    }

    if (Object.keys(payload).length > 0) {
      updateCampaignMutation.mutate(payload);
    }
  };

  const discardCampaignEdits = () => {
    setCampaignEdits({});
    setResetOffAdsetLimits(false);
  };

  const toggleModalRow = (adModerateId) => {
    setModalSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(adModerateId)) next.delete(adModerateId);
      else next.add(adModerateId);
      return next;
    });
  };

  const toggleModalSelectAll = () => {
    const allIds = launchEligibleRows.map((r) => r.ad_moderate_id);
    if (allIds.length > 0 && allIds.every((id) => modalSelectedIds.has(id))) {
      setModalSelectedIds(new Set());
    } else {
      setModalSelectedIds(new Set(allIds));
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        onClose={onClose}
        title="Launch Ads"
        isButtonsRequired={false}
        className="md:w-[80%] lg:w-[70%]"
      >
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <p className="text-xs text-gray-500 mb-1">Category</p>
              <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {(facets?.categories ?? []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Track</p>
              <Autocomplete
                value={trackFilter || null}
                options={facets?.tracks ?? []}
                getOptionLabel={(option) => option || ""}
                isOptionEqualToValue={(option, val) => option === val}
                onChange={(_e, v) => setTrackFilter(v || "")}
                sx={shadcnAutocompleteSx}
                renderInput={(params) => <TextField {...params} placeholder="Select track" />}
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Requested by</p>
              <RequestedByFilter
                value={modalRequestedByFilter}
                onChange={setModalRequestedByFilter}
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Meta Ad Account</p>
              <Select value={selectedMetaAccountId || "none"} onValueChange={(v) => setSelectedMetaAccountId(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select account</SelectItem>
                  {metaAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name || a.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <p className="text-xs text-gray-500 mb-1">Campaign</p>
              <Autocomplete
                value={campaigns.find((c) => c.id === selectedCampaignId) || null}
                options={campaigns}
                getOptionLabel={(c) => c?.name || c?.id || ""}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                onChange={(_e, v) => setSelectedCampaignId(v?.id || "")}
                disabled={!accountIdForCampaigns || campaignsLoading}
                loading={campaignsLoading}
                sx={shadcnAutocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={campaignsLoading ? "Loading..." : "Select campaign"}
                  />
                )}
              />
            </div>
          </div>

          {selectedCampaignId && (
            <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/40">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Campaign settings</span>
                <div className="flex items-center gap-2">
                  {campaignLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                  ) : (
                    <>
                      <span className="text-xs text-gray-500">
                        {campaignDetail?.status || "—"}
                      </span>
                      <Switch
                        checked={(campaignDetail?.status || "").toUpperCase() === "ACTIVE"}
                        disabled={!campaignDetail || updateCampaignMutation.isPending}
                        onCheckedChange={(v) => setPendingStatus(v ? "ACTIVE" : "PAUSED")}
                      />
                    </>
                  )}
                </div>
              </div>
              {campaignDetail && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Daily budget (₹)</p>
                      <Input
                        type="number"
                        min="0"
                        value={campaignDailyBudgetRupees}
                        onChange={(e) => setCampaignEdits((prev) => ({ ...prev, daily_budget_rupees: e.target.value }))}
                        disabled={!campaignDetail?.has_cbo}
                        placeholder={campaignDetail?.has_cbo ? "" : "Not CBO"}
                      />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Bid strategy</p>
                      <Input value="Lowest cost" disabled />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Objective</p>
                      <Input value={campaignDetail?.objective || ""} disabled />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      id="reset-off-adset-limits"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={resetOffAdsetLimits}
                      onChange={(e) => setResetOffAdsetLimits(e.target.checked)}
                      disabled={updateCampaignMutation.isPending || clearOffLimitsMutation.isPending}
                    />
                    <label htmlFor="reset-off-adset-limits" className="text-xs text-gray-700 cursor-pointer">
                      Clear min spend floor on paused adsets in this campaign
                    </label>
                    <Tooltip
                      arrow
                      title="Sets daily_min_spend_target and lifetime_min_spend_target to 0 on PAUSED adsets only (whichever field is actually set on each adset), so their combined floor stops blocking a campaign budget reduction (Meta subcode 1885648). Spend caps (max) are not touched — Meta rejects zero caps with subcode 1885056. Active and newly-launched adsets are also left untouched."
                    >
                      <span className="inline-flex items-center text-gray-400 hover:text-gray-600 cursor-help">
                        <Info className="w-3.5 h-3.5" />
                      </span>
                    </Tooltip>
                  </div>
                  {campaignDirty && (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={discardCampaignEdits}
                        disabled={updateCampaignMutation.isPending || clearOffLimitsMutation.isPending}
                      >
                        Discard
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveCampaign}
                        disabled={updateCampaignMutation.isPending || clearOffLimitsMutation.isPending}
                      >
                        {clearOffLimitsMutation.isPending
                          ? "Clearing limits…"
                          : updateCampaignMutation.isPending
                          ? "Saving…"
                          : resetOffAdsetLimits && !hasCampaignEditPayload
                          ? "Clear limits"
                          : "Save campaign"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {selectedCampaignId && campaignDetail?.has_cbo && (
            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-flex items-center">
              Campaign is CBO · daily budget ₹{Math.floor((campaignDetail.daily_budget || 0) / 100)} · objective {campaignDetail.objective || "—"}
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-3 space-y-3">
            <span className="text-sm font-medium">Adset settings (applies to all selected ads)</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Daily budget per adset (₹)</p>
                <Input
                  type="number"
                  min="0"
                  placeholder={smartAdsetBudgetPlaceholder || (campaignDetail?.has_cbo ? "" : "Optional")}
                  value={adsetDailyBudgetRupees}
                  onChange={(e) => setAdsetDailyBudgetRupees(e.target.value)}
                  disabled={!!campaignDetail?.has_cbo}
                />
                {smartAdsetBudgetPlaceholder && (
                  <p className="text-xs text-gray-500 mt-1">
                    Suggested: ₹{smartAdsetBudgetPlaceholder} (campaign ₹{Math.floor((campaignDetail?.daily_budget || 0) / 100)} ÷ {modalSelectedRows.length} ads)
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Start (optional)</p>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <DatePicker
                      value={adsetStartDate}
                      onChange={setAdsetStartDate}
                      placeholder="Pick date"
                      disabledDates={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }}
                    />
                  </div>
                  <Input type="time" value={adsetStartTime} onChange={(e) => setAdsetStartTime(e.target.value)} className="w-[120px]" />
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">End (optional)</p>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <DatePicker
                      value={adsetEndDate}
                      onChange={setAdsetEndDate}
                      placeholder="Pick date"
                      disabledDates={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (d < today) return true;
                        if (adsetStartDate) {
                          const startDay = new Date(adsetStartDate);
                          startDay.setHours(0, 0, 0, 0);
                          if (d < startDay) return true;
                        }
                        return false;
                      }}
                    />
                  </div>
                  <Input type="time" value={adsetEndTime} onChange={(e) => setAdsetEndTime(e.target.value)} className="w-[120px]" />
                </div>
              </div>
            </div>
            {adsetTimeError && (
              <p className="text-xs text-red-600">{adsetTimeError}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Daily min spend per adset (₹)</p>
                <Input
                  type="number"
                  min="0"
                  placeholder="Optional"
                  value={dailyMinSpendTargetRupees}
                  onChange={(e) => setDailyMinSpendTargetRupees(e.target.value)}
                  disabled={!campaignDetail?.has_cbo}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Daily spend cap per adset (₹)</p>
                <Input
                  type="number"
                  min="0"
                  placeholder="Optional"
                  value={dailySpendCapRupees}
                  onChange={(e) => setDailySpendCapRupees(e.target.value)}
                  disabled={!campaignDetail?.has_cbo}
                />
              </div>
            </div>
            {spendLimitError && (
              <p className="text-xs text-red-600">{spendLimitError}</p>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">Ads to launch</span>
              <div className="flex items-center gap-2">
                <Checkbox
                  size="small"
                  checked={launchEligibleRows.length > 0 && launchEligibleRows.every((r) => modalSelectedIds.has(r.ad_moderate_id))}
                  indeterminate={modalSelectedIds.size > 0 && !launchEligibleRows.every((r) => modalSelectedIds.has(r.ad_moderate_id))}
                  onChange={toggleModalSelectAll}
                  disabled={launchEligibleRows.length === 0}
                />
                <span className="text-xs text-gray-500">Select all</span>
              </div>
            </div>
            <div className="max-h-[220px] overflow-y-auto divide-y divide-gray-100">
              {launchEligibleRows.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No ready ads match these filters on this page.</p>
              ) : (
                launchEligibleRows.map((r) => (
                  <label key={r.ad_moderate_id} className="flex items-start gap-3 px-3 py-2 bg-white hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      size="small"
                      checked={modalSelectedIds.has(r.ad_moderate_id)}
                      onChange={() => toggleModalRow(r.ad_moderate_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm flex-1 min-w-0">
                      <span className="font-medium text-gray-900 block line-clamp-2" title={r.ad_name || ""}>
                        {r.ad_name || `Parent Ad #${r.ad_id ?? "—"} · Moderation #${r.ad_moderate_id}`}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Adset: {r.adset_name || "—"} · Track: {r.track || "—"}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
            <p><span className="text-gray-500">Selected ads count:</span> <span className="font-semibold">{modalLaunchCount}</span></p>
            <p><span className="text-gray-500">Will create:</span> <span className="font-semibold">{modalAdsetCount}</span> adset{modalAdsetCount === 1 ? "" : "s"} · <span className="font-semibold">{modalLaunchCount}</span> ad{modalLaunchCount === 1 ? "" : "s"}</p>
            <p className="break-all"><span className="text-gray-500">Parent Ad IDs:</span> {modalParentAdIds.length ? modalParentAdIds.join(", ") : "—"}</p>
            <p className="break-all"><span className="text-gray-500">Moderation IDs:</span> {modalModerationIds.length ? modalModerationIds.join(", ") : "—"}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                size="small"
                checked={scheduleEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setScheduleEnabled(on);
                  if (!on) {
                    setScheduledDate(null);
                    setScheduledTime("");
                  } else if (!scheduledDate) {
                    // default to 1 hour from now in local time
                    const d = new Date(Date.now() + 60 * 60 * 1000);
                    setScheduledDate(d);
                    setScheduledTime(toTimeValue(d));
                  }
                }}
              />
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Schedule for later</span>
            </label>
            {scheduleEnabled && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-[200px]">
                  <DatePicker
                    value={scheduledDate}
                    onChange={setScheduledDate}
                    placeholder="Pick a date"
                    disabledDates={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today;
                    }}
                  />
                </div>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-[130px]"
                />
              </div>
            )}
            {scheduleEnabled && combineDateTime(scheduledDate, scheduledTime) && (
              <span className="text-xs text-gray-500">
                Will dispatch at {formatDisplayDateTime(combineDateTime(scheduledDate, scheduledTime).toISOString())}
              </span>
            )}
            {scheduleConflictError && (
              <span className="text-xs text-red-600 basis-full">{scheduleConflictError}</span>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => {
                const combined = scheduleEnabled ? combineDateTime(scheduledDate, scheduledTime) : null;
                const isoSchedule = combined ? combined.toISOString() : null;
                const isCbo = !!campaignDetail?.has_cbo;
                const budgetRupees = parseFloat(adsetDailyBudgetRupees);
                const minSpendRupees = parseFloat(dailyMinSpendTargetRupees);
                const spendCapRupees = parseFloat(dailySpendCapRupees);
                bulkLaunchMutation.mutate({
                  ad_moderate_ids: modalSelectedRows.map((r) => r.ad_moderate_id),
                  scheduled_at: isoSchedule,
                  // Per-adset budget applies only in ABO; min/cap apply only in CBO.
                  adset_daily_budget: !isCbo && Number.isFinite(budgetRupees) && budgetRupees > 0
                    ? Math.round(budgetRupees * 100)
                    : null,
                  daily_min_spend_target: isCbo && Number.isFinite(minSpendRupees) && minSpendRupees > 0
                    ? Math.round(minSpendRupees * 100)
                    : null,
                  daily_spend_cap: isCbo && Number.isFinite(spendCapRupees) && spendCapRupees > 0
                    ? Math.round(spendCapRupees * 100)
                    : null,
                  start_time: adsetStartIso,
                  end_time: adsetEndIso,
                });
              }}
              disabled={
                bulkLaunchMutation.isPending
                || modalLaunchCount === 0
                || !accountIdForCampaigns
                || !selectedCampaignId
                || (scheduleEnabled && !combineDateTime(scheduledDate, scheduledTime))
                || !!adsetTimeError
                || !!spendLimitError
              }
              className="bg-[#372e5e] hover:bg-[#2a2349] text-white"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {bulkLaunchMutation.isPending
                ? (scheduleEnabled ? "Scheduling…" : "Launching…")
                : (scheduleEnabled && combineDateTime(scheduledDate, scheduledTime) ? "Schedule launch" : "Launch now")}
            </Button>
          </div>
        </div>
      </CustomModal>

      <ActionConfirmation
        openConf={!!pendingStatus}
        confTitle={pendingStatus === "PAUSED" ? "Pause campaign?" : "Activate campaign?"}
        confirmationCtaText={pendingStatus === "PAUSED" ? "Pause" : "Activate"}
        isLoading={updateCampaignMutation.isPending}
        onClose={() => setPendingStatus(null)}
        handleConfirmation={() => updateCampaignMutation.mutate({ status: pendingStatus })}
      >
        <p className="text-sm text-gray-700">
          {pendingStatus === "PAUSED"
            ? "Ads under this campaign will stop delivering immediately."
            : "The campaign will resume delivering ads."}
        </p>
      </ActionConfirmation>
    </>
  );
}
